import React from 'react';
import { Handle, NodeProps, Position } from '@xyflow/react';
import {
  handleClass,
  handleRow,
  nodeBody,
  nodeHeader,
  nodeShell,
  nodeShellIdle,
  nodeShellSelected,
  smallNodeShell,
} from '../../canvas/nodes/nodeStyles';
import {
  REVIEW_IN_HANDLE,
  ReviewPolicyNodeData,
  ReviewVerdictNodeData,
  reviewVerdictEdgeKind,
  reviewVerdictHandle,
} from '../graphTypes';
import {
  StatusGlyph,
  accentBorder,
  outcomeBadgeClass,
  reviewHandleClass,
  statusOpacity,
} from './reviewerNodeStyles';

/**
 * One thing the reviewer may do about a verdict. Lean by the same rule as the
 * sources: the label, one status glyph, one detail line (the permission that
 * gates it, the attempt budget, why it does not exist yet).
 *
 * On a run, `outcome` says what actually happened — `fired` or `skipped`, read
 * from that run's own verdict stamps. A policy the run says nothing about
 * carries no badge, which is deliberately NOT the same as "it did not fire".
 */
const ReviewPolicyNode: React.FC<NodeProps> = ({ data, selected }) => {
  const nodeData = data as ReviewPolicyNodeData;
  const { policy, status, outcome } = nodeData;

  return (
    <div
      className={`${nodeShell} ${selected ? nodeShellSelected : nodeShellIdle} ${statusOpacity(status)}`}
      style={accentBorder(reviewVerdictEdgeKind(String(policy.verdict)))}
      data-testid={`reviewer-node-policy-${policy.id}`}
      data-status={status}
    >
      <div className={nodeHeader}>
        <span className="truncate normal-case tracking-normal" title={policy.label}>
          {policy.label}
        </span>
        <StatusGlyph status={status} label={policy.label} className="ml-auto" />
      </div>
      {(policy.detail || outcome) && (
        <div className={nodeBody}>
          {outcome && (
            <span className={outcomeBadgeClass} data-testid="reviewer-policy-outcome">
              {outcome}
            </span>
          )}
          {policy.detail && (
            <div className="truncate" title={policy.detail}>
              {policy.detail}
            </div>
          )}
        </div>
      )}
      <Handle
        type="target"
        position={Position.Left}
        id={REVIEW_IN_HANDLE}
        className={reviewHandleClass}
        isConnectable={false}
      />
    </div>
  );
};

/**
 * The fork every policy hangs off: the judge ruled, and the ruling is one of
 * three things. It is its own node rather than three handles on the core so the
 * three flows separate visibly before they reach the policies.
 */
export const ReviewVerdictNode: React.FC<NodeProps> = ({ data, selected }) => {
  const nodeData = data as ReviewVerdictNodeData;
  return (
    <div
      className={`${smallNodeShell} ${selected ? nodeShellSelected : nodeShellIdle}`}
      data-testid="reviewer-node-verdict"
    >
      <div className={nodeHeader}>
        <span>Verdict</span>
      </div>
      <div className={nodeBody}>
        {nodeData.verdicts.map((verdict) => (
          <div
            key={String(verdict)}
            className={`${handleRow} justify-end`}
            data-testid={`reviewer-verdict-${verdict}`}
          >
            <span>{verdict}</span>
            <Handle
              type="source"
              position={Position.Right}
              id={reviewVerdictHandle(String(verdict))}
              className={handleClass}
              isConnectable={false}
            />
          </div>
        ))}
      </div>
      <Handle
        type="target"
        position={Position.Left}
        id={REVIEW_IN_HANDLE}
        className={reviewHandleClass}
        isConnectable={false}
      />
    </div>
  );
};

export default ReviewPolicyNode;
